'use client'

import { classNames } from '@/helpers'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import mitt from 'mitt'
import { TOAST_VARIANTS } from './variants'

export const Toast = () => {
  const [toastData, setToastData] = useState<ToastData | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const styles = TOAST_VARIANTS[toastData?.variant ?? 'default']

  const dismissToast = () => {
    setIsVisible(false)
    // Wait for slide-out animation to complete before hiding
    setTimeout(() => {
      setShowToast(false)
      setToastData(null)
    }, 150) // Match the slideIn animation duration
  }

  const handleToast = (data: ToastData) => {
    setToastData(data)
    setShowToast(true)

    // Trigger slide-in animation after a brief delay
    setTimeout(() => {
      setIsVisible(true)
    }, 10)
  }

  // Listen for toast events from anywhere in the app
  useEffect(() => {
    toastEmitter.on('toast', handleToast)
    return () => {
      toastEmitter.off('toast', handleToast)
    }
  }, [])

  // Auto-dismiss timer
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        dismissToast()
      }, toastData?.duration ?? 5000)

      return () => clearTimeout(timer)
    }
  }, [showToast, toastData])

  // Don't render anything on the server
  if (typeof window === 'undefined' || !showToast || !toastData) {
    return null
  }

  const toastContent = (
    <div
      className={classNames(
        'fixed top-4 right-4 z-50 rounded-md shadow-lg p-4 pr-10 transition-transform duration-150 ease-out',
        isVisible ? 'translate-x-0' : 'translate-x-full',
        styles.container
      )}
    >
      <div className={classNames('font-bold pr-2 text-[15px]', styles.title)}>
        {toastData.title}
      </div>
      {toastData.body && (
        <div className={classNames('pr-2 mt-1 text-[13px]', styles.body)}>
          {toastData.body}
        </div>
      )}

      <button
        onClick={dismissToast}
        className={classNames(
          'absolute top-[13px] right-2 p-1 rounded-full hover:bg-black/10 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer',
          styles.closeButton
        )}
        aria-label="Close"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  )

  return createPortal(toastContent, document.body)
}

interface ToastData {
  title: React.ReactNode
  body?: React.ReactNode
  variant?: 'success' | 'error' | 'default'
  duration?: number
}

export const toastEmitter = mitt<{
  toast: ToastData
}>()
